import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  Icon,
  Popout,
  ProgressBar,
  ReportIssueModal,
  Toast,
  Tooltip,
  VideoPlayerHandle,
} from 'src/components';
import { ActorFace } from 'src/components/ActorFace';
import { LikeVideoButton } from 'src/components/LikeVideoButton';
import { RequestVideoButton } from 'src/components/RequestVideoButton';
import { VideoSpeedControl } from 'src/components/VideoSpeedControl';
import { NotificationContext } from 'src/context/notification';
import { useCurrentLanguagePair, useLearningToggleSettings } from 'src/hooks';
import useSafePush from 'src/hooks/useSafePush';
import { useEligibility } from 'src/hooks/useEligibility';
import { isEnglishLanguage } from 'src/utils/language/isEnglishLanguage';
import { pauseAllVideos } from 'src/apps/LearningSession/utils/pauseAllVideos';
import { scrollToTop } from 'src/utils/browser';
import { useMediaQuery } from 'react-responsive';
import theme from 'src/utils/theme';
import { LearnableValue } from 'src/api/learningSessions/schema';
import {
  LearnableId,
  LearnableValueKind,
  LearningSessionError,
  Mem,
  ProgressPreferences,
  SessionSourceType,
  SessionType,
  StandardSessionType,
  TemplateName,
} from 'src/api/learningSessions/types';
import { ROUTES } from 'src/constants/routes.const';
import { resolveHref } from 'next/dist/client/resolve-href';
import { useRouter } from 'next/router';
import { AI_NOTIFICATION_SESSION } from 'src/constants/ai.const';
import {
  getLocalStorageEntry,
  removeLocalStorageEntry,
  setLocalStorageEntry,
} from 'src/utils/webStorageUtils';
import { TrackingApi } from 'src/api';
import type { TrackingAction, TrackingLocation } from 'src/api/tracking/types';
import { getActiveWordlistId } from 'src/utils/wordlists/getActiveWordlistId';
import {
  PresentationLearnableCard,
  PresentationVideoControl,
  TestLearnableCard,
} from 'src/apps/LearningSession/Common/learnableCards';
import { BaseSession } from 'src/apps/LearningSession/Common/BaseSession';
import { NextActionButton as NAB } from 'src/apps/LearningSession/Common/NextActionButton';
import { useNextAction } from 'src/apps/LearningSession/hooks/useNextAction';
import { useTrackCardScreenView } from 'src/apps/LearningSession/hooks/useTrackCardScreenView';
import { TestResponse } from 'src/apps/LearningSession/Common/learnableCards/TestLearnableCard/Response';
import {
  LearningSessionAction,
  LearningSessionCardState,
} from 'src/apps/LearningSession/hooks/learningSessionReducer';
import * as S from 'src/apps/LearningSession/StandardSession/styles';
import { useExperienceContext } from 'src/context/experience';
import { SHOW_REPETITION_INTRO_MODAL_STORAGE_KEY } from 'src/constants/storageKeys.const';
import { useUserContext } from 'src/context/user';
import { RepeatedWordsModal } from '../Common/RepetitionIntroModal';
import { ExploreDifferentVocabModal } from '../Common/ExploreDifferentVocabModal';
import { LocalProfile } from '../Common/LocalProfile';
import { isExploreDifferentVocabModalSuppressed } from '../Common/ExploreDifferentVocabModal/exploreDifferentVocabModalStorage';
import { Nugget } from '../Common/learnableCards/Nugget';
import { MemGallery } from '../Common/learnableCards/MemGallery';
import { ExampleSentences } from '../Common/learnableCards/ExampleSentences';
import { PronunciationPractice } from '../Common/learnableCards/PresentationLearnableCard/PronunciationPractice';
import { useLearningToolsCoachmark } from './useLearningToolsCoachmark';

const msgs = defineMessages({
  tutorAnyQuestions: { id: 'tutor_buddy_tooltip_1' },
  tutorClickMe: { id: 'tutor_buddy_tooltip_2' },
  tutorName: { id: 'tutor_buddy_name' },
  buddiesTabName: { id: 'ai_buddies_tab_title' },
  replayVideo: { id: 'learning_session_keyboard_shortcuts_replay_media' },
  openLocalProfile: { id: 'local_profile_open', defaultMessage: "View the local's profile" },
  learningTools: {
    id: 'learning_session_learning_tools_CTA',
    defaultMessage: 'Learning Tools',
  },
});

export const DELAY_TIME = 3000;

/** Live-apply the difficult-words "show answer" preference so toggling mid-test
 * immediately hides/shows copy scaffolding (greyed-out answer) without a reload. */
export const applyShowAnswerBeforeTestToResponse = (
  response: TestResponse,
  sessionType: StandardSessionType,
  showAnswerBeforeTest: boolean,
): TestResponse => {
  if (
    sessionType !== SessionType.DIFFICULT_WORDS ||
    showAnswerBeforeTest ||
    (response.templateName !== TemplateName.TYPING &&
      response.templateName !== TemplateName.TAPPING)
  ) {
    return response;
  }

  return { ...response, isCopy: false };
};

export type StandardSessionProps = {
  languagePairId: number;
  sessionType: StandardSessionType;
  error: LearningSessionError | null;
  growthLevel: number | null;
  itemDueForReview: boolean;
  learnableId: number;
  onFeedbackFinished: () => void;
  progressSession: () => void;
  sendCurrentLearningProgress: () => Promise<void>;
  sessionPercentComplete: number;
  title: string;
  currentLearnableProgressPreferences?: ProgressPreferences;
  card: NonNullable<LearningSessionCardState['card']>;
  learningSessionDispatch: (action: LearningSessionAction) => void;
  onItemIgnored: () => void;
  onItemDifficultyChanged: (difficult: boolean) => void;
  isFirstSession?: boolean;
  couldShowRepetitionModal: boolean;
  definition: LearnableValue;
  item: LearnableValue;
  sessionSourceType: SessionSourceType;
  sessionSourceId: number;
  markPreviousAnswerCorrect: (learnableId: LearnableId) => void;
  analyticsSessionId?: string;
  isFromSaved?: boolean;
  userScore?: number | null;
  isSecondLearnable: boolean;
};

export const StandardSession: FC<StandardSessionProps> = ({
  languagePairId,
  sessionType,
  error,
  growthLevel,
  learnableId,
  itemDueForReview,
  onFeedbackFinished,
  progressSession,
  sendCurrentLearningProgress,
  sessionPercentComplete,
  title,
  currentLearnableProgressPreferences,
  card,
  learningSessionDispatch,
  onItemIgnored,
  onItemDifficultyChanged,
  isFirstSession,
  couldShowRepetitionModal,
  definition,
  item,
  sessionSourceType,
  sessionSourceId,
  markPreviousAnswerCorrect,
  analyticsSessionId,
  userScore,
  isSecondLearnable,
  isFromSaved = false,
}) => {
  const router = useRouter();
  const intl = useIntl();
  const { setNotification } = useContext(NotificationContext);
  const { experienceType } = useExperienceContext();
  const { user } = useUserContext();
  const { safePush } = useSafePush();
  const { eligibility, isEligibleForPathways, isEligibleForTracksAndThemes } =
    useEligibility(languagePairId);
  const alreadyKnowMarkCountRef = useRef(0);
  const hasShownExploreVocabModalRef = useRef(false);
  const [showExploreVocabModal, setShowExploreVocabModal] = useState(false);
  const { isFeatureShown, nuggetMode } = useLearningToggleSettings();
  const { languagePair } = useCurrentLanguagePair();
  const isEnglishSource = isEnglishLanguage(languagePair?.sourceLanguageLocale);

  const trackLearningSessionInteraction = useCallback(
    (action: TrackingAction, location: TrackingLocation = 'learning_session') => {
      const wordlistId = getActiveWordlistId();
      const isProUser = !!user?.isPro;

      void TrackingApi.trackUserAction(action, location, languagePairId, isProUser, {
        is_pro_user: String(isProUser),
        ...(wordlistId && { wordlist_id: wordlistId }),
      });
    },
    [languagePairId, user?.isPro],
  );

  // Session-level mems state - persists across learnable switches
  const [sessionMems, setSessionMems] = useState<Map<number, Mem[]>>(new Map());

  const { responseState, data: cardData } = card;
  const isPresentationCard = cardData.kind === 'Presentation';
  const hasVideoBackground = isPresentationCard && !!cardData.videoSrc;
  const hasFullScreenTestVideo = cardData.kind === 'Test' && cardData.prompt.promptType === 'Video';
  const hasFullScreenVideo = hasVideoBackground || hasFullScreenTestVideo;
  const presentationVideoRef = useRef<PresentationVideoControl>(null);
  const testVideoRef = useRef<VideoPlayerHandle>(null);
  const [isPlayingSlowly, setIsPlayingSlowly] = useState(false);
  const [likedVideoSrcs, setLikedVideoSrcs] = useState<ReadonlySet<string>>(() => new Set());
  const [requestedVideoLearnableIds, setRequestedVideoLearnableIds] = useState<ReadonlySet<number>>(
    () => new Set(),
  );
  const currentVideoSrc =
    cardData.kind === 'Presentation'
      ? cardData.videoSrc
      : cardData.kind === 'Test' && cardData.prompt.promptType === 'Video'
        ? cardData.prompt.video
        : null;
  const currentVideoActorIds =
    cardData.kind === 'Presentation'
      ? cardData.videoActorIds
      : cardData.kind === 'Test' && cardData.prompt.promptType === 'Video'
        ? (cardData.prompt.actorIds ?? null)
        : null;
  const presentationActorFace =
    isPresentationCard && eligibility?.eligibleForBlobExperience ? cardData.videoActorFace : null;
  const [isLocalProfileOpen, setIsLocalProfileOpen] = useState(false);
  const isCurrentVideoLiked = !!currentVideoSrc && likedVideoSrcs.has(currentVideoSrc);
  const isVideoRequested = requestedVideoLearnableIds.has(learnableId);
  const toggleLikeCurrentVideo = useCallback(() => {
    if (!currentVideoSrc) return;
    setLikedVideoSrcs(previous => {
      const next = new Set(previous);
      if (next.has(currentVideoSrc)) {
        next.delete(currentVideoSrc);
      } else {
        next.add(currentVideoSrc);
      }
      return next;
    });
  }, [currentVideoSrc]);
  const toggleRequestVideo = useCallback(() => {
    setRequestedVideoLearnableIds(previous => {
      const next = new Set(previous);
      if (next.has(learnableId)) {
        next.delete(learnableId);
      } else {
        next.add(learnableId);
      }
      return next;
    });
  }, [learnableId]);

  useEffect(() => {
    setIsPlayingSlowly(false);
    setIsLocalProfileOpen(false);
  }, [learnableId]);
  const hasInitialMedia =
    isPresentationCard &&
    (!!cardData.videoSrc ||
      cardData.item.kind === LearnableValueKind.AUDIO ||
      cardData.definition.kind === LearnableValueKind.AUDIO ||
      !!cardData.audioSrcs?.length);
  const {
    dismiss: dismissLearningToolsCoachmark,
    markInitialMediaComplete,
    shown: showLearningToolsCoachmark,
  } = useLearningToolsCoachmark({
    userId: user?.id,
    languagePairId,
    sourceLanguageLocale: languagePair?.sourceLanguageLocale,
    sessionType,
    learnableId,
    isSecondLearnable,
    isPresentationCard,
    hasInitialMedia,
  });

  // Initialize mems for this learnable if we haven't seen it before
  useEffect(() => {
    if (cardData.kind === 'Presentation' && cardData.mems && !sessionMems.has(learnableId)) {
      setSessionMems(prev => new Map(prev).set(learnableId, cardData.mems || []));
    }
  }, [learnableId, cardData, sessionMems]);

  // Get current mems for this learnable
  const currentMems = useMemo(() => sessionMems.get(learnableId) || [], [sessionMems, learnableId]);

  // Handlers for mem operations
  const handleMemCreated = useCallback(
    (newMem: Mem) => {
      trackLearningSessionInteraction('mem_creation_created', 'learning_session_create_a_mem');
      setSessionMems(prev => {
        const newMap = new Map(prev);
        const existingMems = newMap.get(learnableId) || [];
        const memWithCreator = { ...newMem, creatorId: user?.id ?? newMem.creatorId };
        newMap.set(learnableId, [memWithCreator, ...existingMems]);
        return newMap;
      });
    },
    [learnableId, trackLearningSessionInteraction, user?.id],
  );

  const handleMemDeleted = useCallback(
    (memId: number) => {
      setSessionMems(prev => {
        const newMap = new Map(prev);
        const existingMems = newMap.get(learnableId) || [];
        newMap.set(
          learnableId,
          existingMems.filter(m => m.id !== memId),
        );
        return newMap;
      });
    },
    [learnableId],
  );

  const handleMemEdited = useCallback(
    (updatedMem: Mem) => {
      setSessionMems(prev => {
        const newMap = new Map(prev);
        const existingMems = newMap.get(learnableId) || [];
        newMap.set(
          learnableId,
          existingMems.map(m =>
            m.id === updatedMem.id ? { ...m, ...updatedMem, creatorId: m.creatorId } : m,
          ),
        );
        return newMap;
      });
    },
    [learnableId],
  );

  const isEligibleForBlobExperience = !!eligibility?.eligibleForBlobExperience;
  const savedTrackingData = useMemo(
    () => (isEligibleForBlobExperience ? { is_from_saved: String(isFromSaved) } : undefined),
    [isEligibleForBlobExperience, isFromSaved],
  );

  useTrackCardScreenView({
    cardData,
    languagePairId,
    isPro: !!user?.isPro,
    sessionSourceType,
    sessionSourceId,
    videoSrc: currentVideoSrc,
    videoActorIds: currentVideoActorIds,
    extraTrackingData: savedTrackingData,
  });

  const { nextActionState, onClick, onKeyUp } = useNextAction({
    card,
    learningSessionDispatch,
    onFeedbackFinished,
    progressSession,
  });

  useEffect(() => {
    if (responseState === 'Processing') {
      pauseAllVideos();
    }
  }, [responseState]);

  useEffect(() => {
    scrollToTop();
  }, [sessionPercentComplete]);

  const presentationCard = cardData.kind === 'Presentation';
  const firstPresentationCard = presentationCard && isFirstSession;
  const showFullPresentationControls = firstPresentationCard || hasVideoBackground;

  const handleItemIgnored = () => {
    onItemIgnored();
    if (
      sessionType !== SessionType.LEARN ||
      hasShownExploreVocabModalRef.current ||
      !isEligibleForPathways
    ) {
      return;
    }

    alreadyKnowMarkCountRef.current += 1;
    if (alreadyKnowMarkCountRef.current >= 2) {
      hasShownExploreVocabModalRef.current = true;
      if (!isExploreDifferentVocabModalSuppressed()) {
        setShowExploreVocabModal(true);
      }
    }
  };

  const handleRemainOnCurrentVocab = () => {
    setShowExploreVocabModal(false);
  };

  const handleExploreDifferentVocab = () => {
    setShowExploreVocabModal(false);
    void (async () => {
      await sendCurrentLearningProgress();
      const destination = isEligibleForTracksAndThemes
        ? ROUTES.wordlists.changeVocabTrack
        : ROUTES.wordlists.main;
      void safePush(destination);
    })();
  };

  const growthLevelAndTogglesComponent = (
    onlyDifficultIcon?: boolean,
    onlyIAKTButton?: boolean,
  ) => (
    <S.GrowthLevelAndToggles
      isItemDueForReview={itemDueForReview}
      growthLevel={growthLevel || 0}
      languagePairId={languagePairId}
      learnableId={learnableId}
      ignored={currentLearnableProgressPreferences?.ignored || false}
      difficult={currentLearnableProgressPreferences?.difficult || false}
      learningSessionDispatch={learningSessionDispatch}
      onItemIgnored={handleItemIgnored}
      onItemDifficultyChanged={onItemDifficultyChanged}
      onlyDifficultIcon={onlyDifficultIcon}
      onlyIAKTButton={onlyIAKTButton}
      onReplayVideo={
        hasFullScreenTestVideo && !onlyDifficultIcon && !onlyIAKTButton
          ? () => testVideoRef.current?.replayAtSpeed(1)
          : undefined
      }
      videoLiked={isCurrentVideoLiked}
      onToggleLikeVideo={
        hasFullScreenTestVideo && !onlyDifficultIcon && !onlyIAKTButton
          ? toggleLikeCurrentVideo
          : undefined
      }
      videoSrc={hasFullScreenTestVideo ? currentVideoSrc : undefined}
    />
  );

  const isMobile = useMediaQuery({ maxWidth: theme.sz.md });

  const eligibleForTutorBuddy = !!eligibility?.eligibleForAiBuddies && experienceType === '2024';
  const shouldShowBuddyNotification =
    !getLocalStorageEntry(AI_NOTIFICATION_SESSION) && eligibleForTutorBuddy;

  const [showBuddyNotification, setShowBuddyNotification] = useState(false);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      /* istanbul ignore next: temporary feature */
      shouldShowBuddyNotification && setShowBuddyNotification(true);
    }, DELAY_TIME);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [shouldShowBuddyNotification]);

  const handleCloseBuddyNotification = () => {
    setShowBuddyNotification(false);
    setLocalStorageEntry(AI_NOTIFICATION_SESSION, 'true');
  };

  const [showRepetitionModal, setShowRepetitionModal] = useState(false);
  const [showReportIssueModal, setShowReportIssueModal] = useState(false);
  useEffect(() => {
    if (
      couldShowRepetitionModal &&
      !showRepetitionModal &&
      cardData.kind === 'Test' &&
      cardData.prompt.promptType === 'Text' &&
      getLocalStorageEntry(SHOW_REPETITION_INTRO_MODAL_STORAGE_KEY) === 'true'
    ) {
      setShowRepetitionModal(true);
    }
  }, [cardData, couldShowRepetitionModal, showRepetitionModal]);
  const handleCloseRepetitionModal = useCallback(() => {
    setShowRepetitionModal(false);
    removeLocalStorageEntry(SHOW_REPETITION_INTRO_MODAL_STORAGE_KEY);
  }, []);

  const handleOpenReportIssueModal = useCallback(() => {
    setShowReportIssueModal(true);
  }, []);

  const handleCloseReportIssueModal = useCallback(() => {
    setShowReportIssueModal(false);
  }, []);

  // Get current learnable display text for the report modal
  const getCurrentLearnableText = useCallback(() => {
    if (cardData.kind === 'Test') {
      // For test cards, show the item and definition
      if (item?.value && definition?.value) {
        return `${item.value} - ${definition.value}`;
      }
    } else if (cardData.kind === 'Presentation') {
      // For presentation cards, show the item and definition
      if (cardData.item?.value && cardData.definition?.value) {
        return `${cardData.item.value} - ${cardData.definition.value}`;
      }
    }
    return undefined;
  }, [cardData, item, definition]);

  const BuddyButton = eligibleForTutorBuddy && (
    <S.BuddyButtonWrapper>
      <Popout.ParentContainer>
        <S.Popout
          show={showBuddyNotification}
          positionX={isMobile ? 'right' : 'left'}
          positionY={isMobile ? 'top' : 'bottom'}
          noBackdrop
          onClickBackdrop={handleCloseBuddyNotification}
        >
          <Toast onClick={handleCloseBuddyNotification} snackBar status="warning" hideIcon>
            <S.Bold>{intl.formatMessage(msgs.tutorAnyQuestions)}</S.Bold>{' '}
            {intl.formatMessage(msgs.tutorClickMe)}
          </Toast>
        </S.Popout>

        <S.BuddyButton
          aria-label={[
            intl.formatMessage(msgs.buddiesTabName),
            intl.formatMessage(msgs.tutorName),
          ].join(' - ')}
          data-testid="BuddyButton"
          target="_blank"
          rel="noopener noreferrer"
          href={resolveHref(router, {
            pathname: ROUTES.ai.dialog,
            query: {
              type: 'tutor',
            },
          })}
        >
          <S.BuddySpeechIcon name="buddy" />
        </S.BuddyButton>
      </Popout.ParentContainer>
    </S.BuddyButtonWrapper>
  );

  const ExampleSentencesSection = ({ overlay = false }: { overlay?: boolean } = {}) => {
    if (
      cardData.kind === 'Test' ||
      !isFeatureShown('sampleSentences') ||
      !cardData.sampleSentences ||
      cardData.sampleSentences.length === 0
    ) {
      return null;
    }

    return (
      <ExampleSentences
        sampleSentences={cardData.sampleSentences}
        learnableValue={cardData.item.value}
        showRomanization={isFeatureShown('romanization')}
        userScore={userScore}
        isEnglishSource={isEnglishSource}
        onAudioClick={() => trackLearningSessionInteraction('example_sentences_audio_clicked')}
        onLightningboltClick={() =>
          trackLearningSessionInteraction('example_sentences_lightningbolt_clicked')
        }
        overlay={overlay}
      />
    );
  };

  const MemGallerySection = ({ overlay = false }: { overlay?: boolean } = {}) => {
    if (cardData.kind === 'Test' || !isFeatureShown('mems')) {
      return null;
    }

    return (
      <MemGallery
        mems={currentMems}
        learnableId={learnableId}
        currentUserId={user?.id ?? null}
        itemValue={cardData.item.value}
        definitionValue={cardData.definition.value}
        targetLanguageName={cardData.item.label}
        sourceLanguageName={cardData.definition.label}
        onCreateClick={() => trackLearningSessionInteraction('mem_creation_clicked')}
        onMemCreated={handleMemCreated}
        onMemDeleted={handleMemDeleted}
        onMemEdited={handleMemEdited}
        showCommunityMems={isFeatureShown('communityMems')}
        overlay={overlay}
      />
    );
  };

  const nuggetCard = cardData.kind !== 'Test' && isFeatureShown('nuggets') && cardData?.nugget && (
    <Nugget
      nugget={cardData?.nugget}
      languagePairId={languagePairId}
      sessionSourceType={sessionSourceType}
      sessionSourceId={sessionSourceId}
      overlay={hasVideoBackground}
      expanded={nuggetMode === 'expanded'}
    />
  );

  const exampleSentencesOverlay = hasVideoBackground
    ? ExampleSentencesSection({ overlay: true })
    : null;
  const memGalleryOverlay = hasVideoBackground ? MemGallerySection({ overlay: true }) : null;
  const learningToolsHasContent = !!exampleSentencesOverlay || !!memGalleryOverlay || !!nuggetCard;

  const LearningToolsOverlay =
    hasVideoBackground && learningToolsHasContent ? (
      <S.LearningToolsPanel data-testid="learning-tools-panel">
        <S.LearningToolsHeading>{intl.formatMessage(msgs.learningTools)}</S.LearningToolsHeading>
        {exampleSentencesOverlay}
        {memGalleryOverlay}
        {nuggetCard}
      </S.LearningToolsPanel>
    ) : null;

  const nextActionButton = (
    <NAB
      state={nextActionState}
      onClick={onClick}
      onKeyUp={onKeyUp}
      isFirstPresentationCard={showFullPresentationControls}
    />
  );

  const mainCard =
    cardData.kind === 'Test' ? (
      <TestLearnableCard
        prompt={cardData.prompt}
        response={applyShowAnswerBeforeTestToResponse(
          cardData.response,
          sessionType,
          isFeatureShown('showAnswerBeforeTest'),
        )}
        attributeValues={cardData.attributeValues}
        postAnswerInfo={cardData.postAnswerInfo}
        responseState={card.responseState}
        userAnswer={card.userAnswer}
        learningSessionDispatch={learningSessionDispatch}
        definition={definition}
        item={item}
        showLiteralTranslation={isFeatureShown('literalTranslation')}
        showRomanization={isFeatureShown('romanization')}
        videoControlRef={hasFullScreenTestVideo ? testVideoRef : undefined}
        footerAction={hasFullScreenTestVideo ? nextActionButton : undefined}
        footerToolbar={
          hasFullScreenTestVideo && isMobile
            ? growthLevelAndTogglesComponent(false, false)
            : undefined
        }
      />
    ) : (
      <PresentationLearnableCard
        {...cardData}
        learnableId={learnableId}
        languagePairId={languagePairId}
        showLiteralTranslation={isFeatureShown('literalTranslation')}
        showRomanization={isFeatureShown('romanization')}
        showSlowAudio={isFeatureShown('showSlowButton')}
        showArabicBreakdown={isFeatureShown('arabicBreakdown')}
        showPitchAccent={isFeatureShown('pitchAccent')}
        showPhoneticSpelling={isEnglishSource && isFeatureShown('phoneticSpelling')}
        onInitialMediaEnded={markInitialMediaComplete}
        onMarkPreviousAnswerCorrect={() => {
          markPreviousAnswerCorrect(learnableId);
          progressSession();
        }}
        videoControlRef={presentationVideoRef}
        onPlayingSlowlyChange={setIsPlayingSlowly}
        isFromSaved={isFromSaved}
      />
    );

  const NextButtons = () => (
    <S.NextButtons
      $fixedWidth={!isMobile && showFullPresentationControls}
      $docked={hasVideoBackground}
    >
      {hasVideoBackground && (
        <PronunciationPractice
          key={learnableId}
          learnableId={learnableId}
          languagePairId={languagePairId}
          hasVideo
          overlay
        />
      )}
      {hasVideoBackground && (
        <S.VideoControls data-testid="presentation-video-controls">
          <S.VideoControlsRow data-testid="presentation-video-controls-row">
            {presentationActorFace && (
              <S.ActorFaceButton
                type="button"
                aria-label={intl.formatMessage(msgs.openLocalProfile)}
                onClick={() => {
                  pauseAllVideos();
                  setIsLocalProfileOpen(true);
                }}
                data-testid="presentation-actor-face-button"
              >
                <ActorFace
                  facePictureSrc={presentationActorFace.facePictureSrc}
                  name={presentationActorFace.name}
                />
              </S.ActorFaceButton>
            )}
            <Tooltip
              placement="bottom"
              tooltip={intl.formatMessage(msgs.replayVideo)}
              disabled={isMobile}
            >
              <S.VideoControlButton
                type="button"
                aria-label={intl.formatMessage(msgs.replayVideo)}
                onClick={() => presentationVideoRef.current?.play()}
                data-testid="presentation-replay-video"
              >
                <Icon name="replayThin" />
              </S.VideoControlButton>
            </Tooltip>
            <VideoSpeedControl
              isPlayingSlowly={isPlayingSlowly}
              onReplay={() => presentationVideoRef.current?.playSlow()}
              languagePairId={languagePairId}
              hideLabel
            />
            {growthLevelAndTogglesComponent(true)}
            <LikeVideoButton
              liked={isCurrentVideoLiked}
              onToggle={toggleLikeCurrentVideo}
              languagePairId={languagePairId}
              learnableId={learnableId}
              videoSrc={currentVideoSrc}
            />
          </S.VideoControlsRow>
        </S.VideoControls>
      )}
      <S.ButtonWrapper $isMobile={isMobile} $fullWidth={hasVideoBackground}>
        {showFullPresentationControls && growthLevelAndTogglesComponent(undefined, true)}
      </S.ButtonWrapper>
      <S.ButtonWrapper $isMobile={isMobile} $fullWidth>
        {nextActionButton}
      </S.ButtonWrapper>
      {isPresentationCard && !hasVideoBackground && (
        <S.ButtonWrapper $isMobile={isMobile} $fullWidth>
          <RequestVideoButton requested={isVideoRequested} onToggle={toggleRequestVideo} />
        </S.ButtonWrapper>
      )}
    </S.NextButtons>
  );

  const dockNextActionWithVideoOptions = hasFullScreenTestVideo;

  const sideControls = (
    <S.ButtonsSideWrapper
      $isCentered={!firstPresentationCard && !hasVideoBackground && !hasFullScreenTestVideo}
      $dockBottom={hasVideoBackground}
      $dockEnd={hasFullScreenTestVideo && !isMobile}
      data-testid={
        hasVideoBackground
          ? 'presentation-side-controls'
          : hasFullScreenTestVideo
            ? 'video-test-side-controls'
            : undefined
      }
    >
      {!dockNextActionWithVideoOptions && NextButtons()}
      {!firstPresentationCard &&
        !hasVideoBackground &&
        growthLevelAndTogglesComponent(false, false)}
      {BuddyButton}
      {!hasVideoBackground && nuggetCard}
    </S.ButtonsSideWrapper>
  );

  return (
    <BaseSession
      title={title}
      error={error}
      loading={false}
      sendCurrentLearningProgress={sendCurrentLearningProgress}
      sessionType={sessionType}
      onReportIssueClick={handleOpenReportIssueModal}
      analyticsSessionId={analyticsSessionId}
      showLearningToolsCoachmark={showLearningToolsCoachmark}
      onDismissLearningToolsCoachmark={dismissLearningToolsCoachmark}
      flushLayout={hasFullScreenVideo}
    >
      <S.ContentLayer $fillViewport={hasFullScreenVideo}>
        {hasVideoBackground && isMobile ? (
          <>
            <S.MobileAboveFold data-testid="mobile-video-above-fold">
              <S.ProgressBarWrapper
                $direction="horizontal"
                $align="center"
                $overlay
                data-testid="session-progress-overlay"
              >
                <ProgressBar
                  percentComplete={sessionPercentComplete}
                  color="yellow"
                  height="medium"
                  backgroundColor="overlay"
                  rounded
                />
              </S.ProgressBarWrapper>
              <S.SessionRow $fillViewport>
                <S.MainColumn>{mainCard}</S.MainColumn>
              </S.SessionRow>
              {sideControls}
            </S.MobileAboveFold>
            {LearningToolsOverlay && (
              <S.MobileLearningToolsSlot data-testid="mobile-learning-tools-slot">
                {LearningToolsOverlay}
              </S.MobileLearningToolsSlot>
            )}
          </>
        ) : (
          <>
            <S.ProgressBarWrapper
              $direction="horizontal"
              $align="center"
              $overlay={hasFullScreenVideo}
              data-testid={hasFullScreenVideo ? 'session-progress-overlay' : undefined}
            >
              <ProgressBar
                percentComplete={sessionPercentComplete}
                color="yellow"
                height="medium"
                {...(hasFullScreenVideo
                  ? { backgroundColor: 'overlay' as const, rounded: true }
                  : {})}
              />
              {isMobile &&
                !hasVideoBackground &&
                !hasFullScreenTestVideo &&
                growthLevelAndTogglesComponent(!!firstPresentationCard)}
            </S.ProgressBarWrapper>
            <S.SessionRow $fillViewport={hasFullScreenVideo}>
              <S.MainColumn>
                {mainCard}
                {LearningToolsOverlay}
              </S.MainColumn>
              {!isMobile &&
                firstPresentationCard &&
                !hasVideoBackground &&
                growthLevelAndTogglesComponent(true)}

              {!isMobile && !hasVideoBackground && sideControls}
            </S.SessionRow>
            {hasVideoBackground && sideControls}
          </>
        )}
        {!hasVideoBackground && ExampleSentencesSection()}
        {!hasVideoBackground && MemGallerySection()}
        {isMobile && !hasVideoBackground && nuggetCard ? (
          <S.MobileNuggetWrapper data-testid="mobile-nugget-wrapper">
            {nuggetCard}
          </S.MobileNuggetWrapper>
        ) : null}
        {isMobile && !hasVideoBackground && (
          <S.ButtonWrapperFooter
            $hasStickyButton={presentationCard}
            $hasOneRow={!eligibleForTutorBuddy}
            $transparent={hasFullScreenTestVideo}
            data-testid="learning-session-footer"
          >
            {BuddyButton}
            {!hasFullScreenTestVideo && NextButtons()}
          </S.ButtonWrapperFooter>
        )}
        <ExploreDifferentVocabModal
          isOpen={showExploreVocabModal}
          languagePairId={languagePairId}
          onRemain={handleRemainOnCurrentVocab}
          onExplore={handleExploreDifferentVocab}
        />
        <RepeatedWordsModal isOpen={showRepetitionModal} onClose={handleCloseRepetitionModal} />
        {presentationActorFace && (
          <LocalProfile
            isOpen={isLocalProfileOpen}
            onClose={() => setIsLocalProfileOpen(false)}
            languagePairId={languagePairId}
            actor={presentationActorFace}
          />
        )}
        <ReportIssueModal
          isOpen={showReportIssueModal}
          onClose={handleCloseReportIssueModal}
          learnableId={learnableId}
          learnableText={getCurrentLearnableText()}
          isEnglishSource={isEnglishSource}
          onSuccess={() => {
            setNotification({
              text: intl.formatMessage({ id: 'report_issue_modal_success' }),
              status: 'success',
            });
          }}
        />
      </S.ContentLayer>
    </BaseSession>
  );
};
